import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { publishSolrQueryUrl } from '../utils';

interface QueryLinkProps {
  url: string;
  onQueryClick?: (url: string) => void;
}

/**
 * A "blink" component that displays the generated search query URL
 * as an eye-catching, clickable chip/card. This is the URL that gets
 * captured and passed on to the search feature of the app.
 *
 * On mount/update, publishes the URL to the eventBus so that host pages
 * (e.g., MockHostSearchPage or other subscribers) can listen and fetch
 * Solr results.
 */
const QueryLink: React.FC<QueryLinkProps> = ({ url, onQueryClick }) => {
  // eslint-disable-next-line no-console
  console.log('[QueryLink] Component rendered with URL:', url);

  // Publish URL to eventBus when it changes
  useEffect(() => {
    if (url) {
      // eslint-disable-next-line no-console
      console.log('[QueryLink] Publishing Solr query URL:', url);
      publishSolrQueryUrl(url);
      // Also dispatch a global DOM event so other bundles can subscribe
      // without importing the module singleton directly.
      if (typeof window !== 'undefined') {
        try {
          // eslint-disable-next-line no-console
          console.log(
            '[QueryLink] Dispatching global solr-query-url event with URL:',
            url,
          );
          window.dispatchEvent(
            new CustomEvent('solr-query-url', {
              detail: { url, ts: Date.now() },
            }),
          );
          // eslint-disable-next-line no-console
          console.log('[QueryLink] Global event dispatched successfully');
        } catch (e) {
          // ignore failures in environments without CustomEvent
          // eslint-disable-next-line no-console
          console.error('[QueryLink] Failed to dispatch global event:', e);
        }
      }
    }
  }, [url]);

  const handleClick = (e: React.MouseEvent) => {
    if (onQueryClick) {
      e.preventDefault();
      onQueryClick(url);
    }
  };

  return (
    <div className="ves-query-link-container">
      <div className="ves-query-link-label">Generated Search Query</div>
      <a
        href={url}
        onClick={handleClick}
        className="ves-query-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="ves-query-link-indicator">
          <span className="ves-query-link-ping" />
          <span className="ves-query-link-dot" />
        </span>
        <span className="ves-query-link-url">{url}</span>
        <FontAwesomeIcon
          icon={faExternalLinkAlt}
          className="ves-query-link-icon"
        />
      </a>
    </div>
  );
};

export default QueryLink;
